// Dedicated service pages for Kool Plumbing — rendered at /services/:slug

export const SERVICES = [
  {
    slug: "septic-tank-replacement",
    name: "Septic Tank Replacement",
    short: "Septic Replacement",
    icon: "layers",
    flagship: true,
    image: "https://images.pexels.com/photos/32588548/pexels-photo-32588548.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tagline: "Complete septic system replacement, engineered to last 25+ years.",
    metaTitle: "Septic Tank Replacement in Miami-Dade & Broward | Kool Plumbing",
    metaDesc: "Expert septic tank replacement across Miami-Dade & Broward. Soil testing, county permitting, premium tanks & drain fields — all in-house. Free quotes. License #CFC1432999. Call (786) 622-4963.",
    intro: [
      "A failing septic tank is more than an inconvenience — it's a health hazard, an environmental liability, and a fast track to costly county fines. When repairs no longer cut it, Kool Plumbing delivers complete, code-compliant septic tank replacement that protects your property and your family for decades.",
      "As a Florida State Certified Plumbing Contractor (License #CFC1432999), we manage every stage of your replacement in-house — from the first soil percolation test to the final county sign-off — so you get one accountable team, transparent pricing, and zero runaround.",
    ],
    bullets: [
      "Comprehensive soil percolation testing to size your system correctly",
      "Full Miami-Dade & Broward County permitting and engineering handled for you",
      "Premium precast concrete or poly tanks built for Florida's water table",
      "Drain field reclamation with advanced gravel-less chamber technology",
      "Complete excavation, installation, and yard restoration",
      "Aerobic & conventional systems — we recommend the right fit for your lot",
    ],
    signsTitle: "Signs you need a septic tank replacement",
    signs: [
      "Sewage backing up into sinks, tubs, or toilets",
      "Standing water or soggy patches over the drain field",
      "Foul odors in the yard or near the tank",
      "Slow drains throughout the house",
      "Bright green, spongy grass above the septic area",
      "A tank that's 25+ years old or repeatedly failing",
    ],
    process: [
      ["Free Estimate", "Use our online estimator or book a free on-site inspection and quote."],
      ["Testing & Permits", "We perform soil perc testing and handle all county permitting and engineering."],
      ["Excavation & Install", "Old tank removed; premium new tank and drain field installed to code."],
      ["Restoration & Sign-Off", "We restore your yard and secure final environmental approval."],
    ],
    faqs: [
      ["How much does septic tank replacement cost?", "Most residential replacements range from about $6,500 to $18,000 depending on tank size, soil conditions, drain field work, and permitting. Use our free estimator for an instant range, then we confirm with a free on-site quote."],
      ["How long does the replacement take?", "Once permits are approved, most replacements are completed in 2–4 days including excavation, tank installation, drain field work, and yard restoration."],
      ["Do you handle the permits?", "Yes — we manage all soil testing, engineering, and Miami-Dade/Broward County permitting in-house. You never deal with the paperwork."],
      ["How long will my new system last?", "A properly sized, professionally installed system with routine pumping typically lasts 25–40 years."],
    ],
  },
  {
    slug: "drain-field-repair",
    name: "Drain Field Repair & Restoration",
    short: "Drain Field Repair",
    icon: "wrench",
    image: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    tagline: "Restore failing drain fields before they require full replacement.",
    metaTitle: "Drain Field Repair & Restoration | Kool Plumbing Miami-Dade & Broward",
    metaDesc: "Slow drains, soggy yard, or backups? Kool Plumbing repairs & restores failing septic drain fields across Miami-Dade & Broward. Free diagnosis. License #CFC1432999. Call (786) 622-4963.",
    intro: [
      "Your drain field is the most expensive and vulnerable part of your septic system. When it starts to fail — slow drains, soggy ground, odors — acting early can save you thousands and often avoids a full system replacement.",
      "Kool Plumbing diagnoses the real cause of drain field trouble and restores flow using biological rejuvenation, mechanical aeration, and targeted repairs — bringing your system back to healthy operation whenever possible.",
    ],
    bullets: [
      "Full diagnostic to pinpoint the cause of drain field failure",
      "Biological rejuvenation to break down biomat clogging",
      "Mechanical aeration to restore soil absorption",
      "Line and distribution box repair or replacement",
      "Honest assessment — we tell you when repair beats replacement",
    ],
    signsTitle: "Signs your drain field needs attention",
    signs: [
      "Slow drainage or gurgling toilets",
      "Wet, spongy, or smelly areas over the field",
      "Backups after heavy rain",
      "Unusually lush, green grass over the drain lines",
    ],
    process: [
      ["Free Diagnosis", "We inspect and identify exactly why your field is struggling."],
      ["Repair Plan", "You get a clear, upfront plan and price — repair or replace."],
      ["Restoration", "We rejuvenate, aerate, or repair to restore proper flow."],
      ["Follow-Up", "Maintenance guidance to keep your system healthy long-term."],
    ],
    faqs: [
      ["Can a drain field really be repaired, or must it be replaced?", "Many drain fields can be restored if caught early through aeration and biological treatment. We always assess repair options first and only recommend replacement when it's truly necessary."],
      ["How much does drain field repair cost?", "Repairs typically run a fraction of a full replacement. We provide a free diagnosis and exact quote before any work begins."],
      ["How fast can you come out?", "We offer prompt scheduling and 24/7 emergency response for active backups across Miami-Dade and Broward."],
    ],
  },
  {
    slug: "septic-pumping",
    name: "Septic Pumping & Maintenance",
    short: "Septic Pumping",
    icon: "shield",
    image: "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    tagline: "Routine pumping that prevents expensive system failures.",
    metaTitle: "Septic Tank Pumping & Maintenance | Kool Plumbing Miami-Dade & Broward",
    metaDesc: "Protect your septic system with professional pumping & maintenance from Kool Plumbing. Serving Miami-Dade & Broward. Recommended every 3–5 years. Call (786) 622-4963.",
    intro: [
      "Routine septic pumping is the single best way to protect your investment. Pumping removes the solids that, left unchecked, flow into and destroy your drain field — the costliest part of your system to replace.",
      "Kool Plumbing provides clean, professional septic pumping and maintenance on a schedule that keeps your system running trouble-free, typically every 3 to 5 years depending on household size and usage.",
    ],
    bullets: [
      "Complete tank pumping and solids removal",
      "Full system inspection while we're on-site",
      "Baffle, filter, and lid condition check",
      "Maintenance scheduling so you never forget",
      "Early warning on issues before they become emergencies",
    ],
    signsTitle: "When to schedule a pump-out",
    signs: [
      "It's been 3+ years since your last pumping",
      "Slow drains starting to appear",
      "Occasional odors near the tank",
      "You're buying or selling the property",
    ],
    process: [
      ["Book a Visit", "Schedule online or by phone at your convenience."],
      ["Inspect & Locate", "We locate and access your tank and assess its condition."],
      ["Pump & Clean", "Complete solids removal and a clean, tidy job site."],
      ["Report & Schedule", "You get a condition report and your next service date."],
    ],
    faqs: [
      ["How often should I pump my septic tank?", "Most households need pumping every 3–5 years, though larger families or smaller tanks may need it more often. We'll recommend the right interval for you."],
      ["What happens if I never pump my tank?", "Solids overflow into the drain field, clogging it permanently and forcing an expensive full replacement. Routine pumping is far cheaper than replacement."],
      ["Do you inspect while pumping?", "Yes — every pump-out includes a full inspection of the tank, baffles, and filter so we catch problems early."],
    ],
  },
  {
    slug: "emergency-plumbing",
    name: "24/7 Emergency Plumbing",
    short: "Emergency Plumbing",
    icon: "alert",
    image: "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
    tagline: "Fast, around-the-clock response when disaster strikes.",
    metaTitle: "24/7 Emergency Plumbing & Septic Service | Kool Plumbing Miami-Dade & Broward",
    metaDesc: "Septic backup or burst pipe? Kool Plumbing offers 24/7 emergency plumbing & septic response across Miami-Dade & Broward. Licensed #CFC1432999. Call (786) 622-4963 now.",
    intro: [
      "Plumbing emergencies don't wait for business hours — and neither do we. A septic backup, burst pipe, or overflowing system can cause thousands in damage every hour it's left unaddressed.",
      "Kool Plumbing provides true 24/7 emergency response across Miami-Dade and Broward. One call gets a licensed technician on the way fast to contain the problem and protect your home.",
    ],
    bullets: [
      "24/7 live dispatch — nights, weekends, and holidays",
      "Rapid response for septic backups and overflows",
      "Burst pipe, major leak, and sewer line emergencies",
      "Emergency drain clearing and line jetting",
      "Damage containment and a clear repair plan on the spot",
    ],
    signsTitle: "Call us immediately if you have",
    signs: [
      "Sewage backing up into your home",
      "A burst or leaking main line",
      "An overflowing septic tank",
      "No water or a major fixture failure",
    ],
    process: [
      ["Call (786) 622-4963", "Reach our live 24/7 dispatch any time, day or night."],
      ["Rapid Dispatch", "A licensed technician is sent to you as fast as possible."],
      ["Contain & Diagnose", "We stop the damage and diagnose the root cause."],
      ["Repair", "Clear, upfront pricing and a permanent fix."],
    ],
    faqs: [
      ["Do you really answer 24/7?", "Yes. Our emergency line (786) 622-4963 is staffed around the clock, including nights, weekends, and holidays."],
      ["What counts as a plumbing emergency?", "Sewage backups, burst pipes, overflowing septic tanks, major leaks, and total loss of water all warrant immediate emergency service."],
      ["How fast can you get here?", "We prioritize emergencies and dispatch the nearest available licensed technician throughout Miami-Dade and Broward."],
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
